<template>
    <div>
        <div class="container-fluid pt-4">
            <b-modal id="StoreAlertEdit" title="Admin" @ok="close()" ok-only>
                <p class="my-4">{{ message }}</p>
            </b-modal>
            <hr>
            <div class="row product-adding">
                <div class="col-xl-12">
                    <div class="row product-adding">
                        <div class="col-xl-6">
                            <div class="digital-add needs-validation">
                                <h4>Configuration for BigBuy</h4>
                                <p class="mb-4">
                                    Enter the API keys provided by BigBuy to import products directly
                                    into this store. Both environments share the same API, only the
                                    key and the endpoint change.
                                </p>
                                <div class="form-group">
                                    <b-form-checkbox v-model="form.bigbuyEnabled">Enable BigBuy product import?</b-form-checkbox>
                                </div>
                                <div class="form-group">
                                    <b-form-checkbox v-model="form.bigbuyShipping">Live BigBuy shipping rates at checkout?</b-form-checkbox>
                                    <small class="text-muted">The storefront shows the real BigBuy carriers and costs
                                        instead of the fixed shipping price (Store &gt; Shipping).</small>
                                </div>
                                <div class="form-group">
                                    <label class="col-form-label pt-0">Environment</label>
                                    <b-form-select v-model="form.bigbuyMode" :options="modeOptions"></b-form-select>
                                </div>
                                <div class="form-group">
                                    <label class="col-form-label pt-0">Production API key</label>
                                    <input class="form-control" type="text" v-model="form.bigbuyApiKey"
                                        placeholder="Key for api.bigbuy.eu" />
                                </div>
                                <div class="form-group">
                                    <label class="col-form-label pt-0">Sandbox API key</label>
                                    <input class="form-control" type="text" v-model="form.bigbuySandboxKey"
                                        placeholder="Key for api.sandbox.bigbuy.eu" />
                                </div>
                                <div class="form-group">
                                    <label class="col-form-label pt-0">Catalog language (isoCode)</label>
                                    <b-form-select v-model="form.bigbuyIso" :options="isoOptions"></b-form-select>
                                </div>
                                <div class="form-group">
                                    <label class="col-form-label pt-0">Margin (%) applied on wholesale price</label>
                                    <input class="form-control" type="number" min="0" step="1" v-model.number="form.bigbuyMargin" />
                                    <small class="text-muted">Imported price = (wholesale price + EU shipping below) × (1 + this margin / 100).</small>
                                </div>
                                <div class="form-group">
                                    <label class="col-form-label pt-0">Worst-case EU shipping added to the price</label>
                                    <input class="form-control" type="number" min="0" step="0.01" v-model.number="form.bigbuyShippingEU" />
                                    <small class="text-muted">Highest BigBuy shipping cost among your European destinations.
                                        It is added to the wholesale price before the margin, so every EU order is covered —
                                        then enable Free shipping (Store &gt; Shipping): the customer pays no separate shipping.</small>
                                    <small v-if="form.bigbuyShipping && form.bigbuyShippingEU > 0" class="text-warning d-block">
                                        Live BigBuy shipping rates at checkout is also on: the customer would pay the
                                        shipping twice. Leave this at 0 or untick the live rates option.
                                    </small>
                                </div>
                                <div class="form-group mb-0">
                                    <button type="button" class="btn btn-outline-primary" @click="testConnection"
                                        :disabled="testing">
                                        {{ testing ? "Testing..." : "Test connection" }}
                                    </button>
                                    <p v-if="testMessage" class="mt-3 mb-0" :class="testOk ? 'text-success' : 'text-danger'">
                                        {{ testMessage }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="col-xl-6">
                            <div class="digital-add needs-validation">
                                <h4>Import products</h4>
                                <p>
                                    Once the keys are saved, open the import page to browse the BigBuy
                                    catalog and add products to this store.
                                </p>
                                <router-link to="/products/bigbuy-import" class="btn btn-secondary">
                                    Open BigBuy import page
                                </router-link>
                                <p class="mt-4 mb-1">Active API endpoint: <code>{{ apiBase }}</code></p>
                                <p class="mb-1">
                                    Production key:
                                    <strong :class="form.bigbuyApiKey ? 'text-success' : 'text-muted'">
                                        {{ form.bigbuyApiKey ? "saved" : "missing" }}
                                    </strong>
                                </p>
                                <p class="mb-0">
                                    Sandbox key:
                                    <strong :class="form.bigbuySandboxKey ? 'text-success' : 'text-muted'">
                                        {{ form.bigbuySandboxKey ? "saved" : "missing" }}
                                    </strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-xl-12 pb-3 pt-3">
                    <div class="form-group mb-0">
                        <div class="product-buttons text-right">
                            <button type="button" class="btn btn-primary" @click="save">
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import firebase from '@/utils/firestore-shim';
import Cookies from 'js-cookie';
import axios from 'axios';
import { apiUrl, uploadHeaders } from '../../../utils/imageUpload.js';
export default {
    data() {
        return {
            form: {},
            data: {},
            id: '',
            message: "",
            error: 0,
            testing: false,
            testOk: false,
            testMessage: "",
            modeOptions: [
                { value: 'sandbox', text: 'Sandbox (tests)' },
                { value: 'production', text: 'Production (live catalog)' }
            ],
            isoOptions: [
                { value: 'en', text: 'English (en)' },
                { value: 'fr', text: 'Français (fr)' },
                { value: 'es', text: 'Español (es)' },
                { value: 'pt', text: 'Português (pt)' },
                { value: 'it', text: 'Italiano (it)' },
                { value: 'de', text: 'Deutsch (de)' },
                { value: 'nl', text: 'Nederlands (nl)' },
                { value: 'pl', text: 'Polski (pl)' }
            ]
        };
    },
    computed: {
        apiBase() {
            return this.form.bigbuyMode === 'production'
                ? 'https://api.bigbuy.eu'
                : 'https://api.sandbox.bigbuy.eu';
        }
    },
    async mounted() {
        var data = await this.getStoresData();
        var store = data && data.length ? data[0] : null;
        if (!store) {
            this.$router.push('/dashboard/default');
            return;
        }
        // Defaults are set BEFORE the object is assigned to this.form, so the
        // new fields are reactive (Vue 2 cannot detect properties added later).
        if (store.bigbuyEnabled === undefined) store.bigbuyEnabled = false;
        if (store.bigbuyShipping === undefined || store.bigbuyShipping === null) store.bigbuyShipping = false;
        if (!store.bigbuyMode) store.bigbuyMode = 'sandbox';
        if (store.bigbuyApiKey === undefined || store.bigbuyApiKey === null) store.bigbuyApiKey = '';
        if (store.bigbuySandboxKey === undefined || store.bigbuySandboxKey === null) store.bigbuySandboxKey = '';
        if (!store.bigbuyIso) store.bigbuyIso = 'en';
        var margin = Number(store.bigbuyMargin);
        store.bigbuyMargin = (store.bigbuyMargin === undefined || store.bigbuyMargin === null ||
            store.bigbuyMargin === '' || !isFinite(margin)) ? 30 : margin;
        var shippingEU = Number(store.bigbuyShippingEU);
        store.bigbuyShippingEU = (store.bigbuyShippingEU === undefined || store.bigbuyShippingEU === null ||
            store.bigbuyShippingEU === '' || !isFinite(shippingEU)) ? 0 : shippingEU;
        this.form = store;
    },
    methods: {
        async save() {
            if ((this.form.title == "") || (this.form.host == "") || (this.form.logo == "") || (this.form.symbol == "") || (this.form.rate == "")) {
                this.message = "Please complete all required fields!";
                this.error = 1;
                this.$bvModal.show('StoreAlertEdit');
                return false;
            } else {
                this.error = 0;
            }
            var margin = Number(this.form.bigbuyMargin);
            this.form.bigbuyMargin = (this.form.bigbuyMargin === '' || !isFinite(margin)) ? 0 : margin;
            var shippingEU = Number(this.form.bigbuyShippingEU);
            this.form.bigbuyShippingEU = (this.form.bigbuyShippingEU === '' || !isFinite(shippingEU) || shippingEU < 0) ? 0 : shippingEU;
            if (this.form.bigbuyEnabled && this.form.bigbuyMode === 'production' && !this.form.bigbuyApiKey) {
                this.message = "The production API key is required when BigBuy is enabled in production mode.";
                this.error = 1;
                this.$bvModal.show('StoreAlertEdit');
                return false;
            }
            if (this.form.bigbuyEnabled && this.form.bigbuyMode === 'sandbox' && !this.form.bigbuySandboxKey) {
                this.message = "The sandbox API key is required when BigBuy is enabled in sandbox mode.";
                this.error = 1;
                this.$bvModal.show('StoreAlertEdit');
                return false;
            }
            let ref = await firebase.firestore().collection('Sites').doc(this.form.host).set(this.form)
                .then(() => {
                    this.message = "Document successfully written!";
                    this.error = 0;
                    this.$bvModal.show('StoreAlertEdit');
                })
                .catch((error) => {
                    this.message = "Error writing document: " + error;
                    this.error = 1;
                    this.$bvModal.show('StoreAlertEdit');
                });
        },
        // Tests the SAVED settings through the Hermes API (the BigBuy key
        // never reaches the browser): GET /api/stores/{host}/bigbuy/test.
        // Save the form before testing so the server sees the saved keys.
        async testConnection() {
            this.testOk = false;
            this.testMessage = "";
            if (!this.form.host) {
                this.testMessage = "No store selected.";
                return false;
            }
            this.testing = true;
            try {
                var res = await axios.get(apiUrl + '/api/stores/' + encodeURIComponent(this.form.host) + '/bigbuy/test', {
                    headers: uploadHeaders(),
                    timeout: 20000
                });
                var count = res.data && res.data.languages ? res.data.languages : 0;
                this.testOk = true;
                this.testMessage = "Connection successful (" + (res.data && res.data.mode ? res.data.mode : "?") + ")" +
                    (count > 0 ? " — " + count + " languages available." : ".");
            } catch (error) {
                this.testOk = false;
                var detail = error && error.response && error.response.data && error.response.data.error
                    ? String(error.response.data.error) : "";
                var status = error.response ? error.response.status : 0;
                if (status === 401) {
                    this.testMessage = detail || "Hermes API key invalid or missing (x-api-key). Check config.json.";
                } else if (status === 409) {
                    this.testMessage = detail || "BigBuy is disabled or has no key saved for the selected environment. Save the settings first.";
                } else if (status === 429) {
                    this.testMessage = "BigBuy rate limit reached (HTTP 429), try again in a few minutes.";
                } else if (status === 502 || status === 504) {
                    this.testMessage = detail || "BigBuy returned an error or did not answer in time.";
                } else if (status) {
                    this.testMessage = detail || "Request returned HTTP " + status + ".";
                } else {
                    this.testMessage = "Unable to reach the Hermes API (" + (error.message || "network error") + ").";
                }
            } finally {
                this.testing = false;
            }
        },
        close() {
            this.$bvModal.hide('StoreAlertEdit');
        },
        async getStoresData() {
            let listData = [];
            var ProductRef = await firebase.firestore().collection("Sites")
                .where("host", "==", this.getStore())
                .get()
                .then((querySnapshot) => {
                    querySnapshot.forEach((doc) => {
                        this.id = doc.id;
                        listData.push(
                            doc.data()
                        );
                    });
                });
            return listData;

        },
        getStore() {
            const host = Cookies.get('hostCookie');
            if (host) {
                return host;
            } else {
                this.$router.push('/dashboard/default');
            }
        }
    }
};
</script>
<style lang="scss" scoped>
</style>
